import { matchPath } from "react-router-dom";
import { SUPEROPS_CONFIG } from "../config/SuperOpsConfig";
import { isInIframe } from "@/Utils/superops/embedDetection";
import { getAppPathname } from "@/Utils/routing/getAppPathname";

/**
 * Utility functions for detecting embedded mode
 * Embedded mode is triggered by either iframe context or ?embed=true query parameter
 */

/**
 * Get list of domains allowed to embed this app in iframe
 * @returns array of allowed domain strings
 */
export const getAllowedDomains = (): string[] => {
	return SUPEROPS_CONFIG.ALLOWED_IFRAME_DOMAINS;
};

/**
 * Check if the parent domain is allowed to embed this app
 * @returns true if parent domain is in allowlist
 */
export const isAllowedDomain = (): boolean => {
	try {
		// Use document.referrer - safe to access cross-origin
		const referrer = document.referrer;

		if (!referrer) {
			// No referrer available - default to allow (permissive for now)
			return true;
		}

		const parentOrigin = new URL(referrer).origin;
		const allowedDomains = getAllowedDomains();

		// Check exact matches first
		if (allowedDomains.includes(parentOrigin)) {
			return true;
		}

		// Check wildcard patterns
		return allowedDomains.some(pattern => {
			if (pattern.includes("*")) {
				// Convert wildcard pattern to regex
				// https://*.superops.com -> https://.*\.superops\.com
				const regexPattern = pattern
					.replace(/[.]/g, "\\.") // Escape dots
					.replace(/\*/g, ".*"); // Convert * to .*

				const regex = new RegExp(`^${regexPattern}$`);
				return regex.test(parentOrigin);
			}
			return false;
		});
	} catch (error) {
		// If any error parsing referrer, default to allow (permissive for now)
		console.error("Error checking domain:", error);
		return true;
	}
};

/**
 * Check if current route is allowed in any iframe (bypasses domain restrictions)
 * @param pathname - The current pathname to check
 * @returns true if route should work in any iframe
 */
export const isUniversalIframeRoute = (pathname: string): boolean => {
	return SUPEROPS_CONFIG.UNIVERSAL_IFRAME_ROUTES.includes(pathname);
};

/**
 * Check if iframe embedding is allowed for current context
 * @returns true if not in iframe, or if in iframe with proper authorization
 */
export const isAllowedIframe = (): boolean => {
	// If not in iframe, always allow
	if (!isInIframe()) {
		return true;
	}

	// Allow universal routes in all iframes (no domain restrictions)
	if (isUniversalIframeRoute(getAppPathname())) {
		return true;
	}

	// All other routes require embed param and domain validation
	const urlParams = new URLSearchParams(window.location.search);
	const hasEmbedParam = urlParams.get("embed") === "true";

	return hasEmbedParam || isAllowedDomain();
};

/**
 * Check if current route is allowed in embedded mode
 * Only opportunity, document, and essential auth routes are permitted
 */
export const isAllowedEmbedRoute = (pathname: string): boolean => {
	// Extract just the pathname if it includes query parameters
	const cleanPathname = pathname.split("?")[0];

	return SUPEROPS_CONFIG.ALLOWED_EMBED_ROUTES.some(config =>
		matchPath(cleanPathname, config)
	);
};

/**
 * Get the redirect URL for unauthorized embedded routes
 */
export const getEmbedErrorRoute = (): string => {
	return SUPEROPS_CONFIG.ROUTES.ERROR;
};
